import { Box, CircularProgress, IconButton, Modal, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { getServerSummaryList } from '../../../../services/candidateService';
import { SampleValue } from './dashboardPlaceholders';

const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT);

// Columns exactly as in the approved design (CET_PGP_Dashboard.html).
// sample: true = not returned by central yet (next phase)
const columns = [
    { id: 'srNo', label: 'Sr No', minWidth: 60 },
    { id: 'examCode', label: 'Exam Code', minWidth: 120 },
    { id: 'regNo', label: 'Reg No', minWidth: 110 },
    { id: 'rollNo', label: 'Roll Number', minWidth: 120 },
    { id: 'candidateName', label: 'Name', minWidth: 140 },
    { id: 'attempted', label: 'Attempted', minWidth: 90, sample: true },
    { id: 'usedTime', label: 'Duration', minWidth: 100 },
    { id: 'ipAddress', label: 'Current IP', minWidth: 120 },
    { id: 'macAddress', label: 'MAC Address', minWidth: 150 },
];

export const CenterDetailsModal = ({ center, onClose }) => {

    const [candidates, setCandidates] = useState([]);
    const [examCode, setExamCode] = useState('');
    const [maxPages, setMaxPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setCurrentPage(1);
    }, [center]);

    useEffect(() => {
        const fetchCandidates = async () => {
            if (!center) {
                return;
            }
            setLoading(true);
            try {
                const res = await getServerSummaryList(center.projectMasterId, center.serverId, center.examHallId, currentPage, limit, center.status);
                if (res.code === 'OK') {
                    setCandidates(res.data.rows);
                    setExamCode(res.data.courseName ?? '');
                    setMaxPages(Math.max(1, Math.ceil(res.data.count / limit)));
                }
            } catch (error) {
                console.log('There is an error while fetching center candidates', error);
            }
            setLoading(false);
        }
        fetchCandidates();
    }, [center, currentPage]);

    const renderTableCell = (column, candidate, candidateIndex) => {
        switch (column.id) {
            case 'srNo':
                return (currentPage - 1) * limit + candidateIndex + 1;
            case 'examCode':
                return examCode || '-';
            case 'attempted':
                return <SampleValue>-</SampleValue>;
            default:
                return candidate?.[column.id] ?? '-';
        }
    }

    return (
        <Modal
            hideBackdrop={true}
            className='backdrop'
            open={!!center}
            onClose={onClose}
        >
            <Box className='modal-container' sx={{
                maxWidth: 1100,
                p: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                    <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, color: 'primary.main' }}>
                        Center Details · {center?.hallName} ({center?.serverName}){center?.statusLabel ? ` · ${center.statusLabel}` : ''}
                    </Typography>
                    <IconButton aria-label='Close' onClick={onClose} sx={{ fontSize: 22, lineHeight: 1 }}>
                        ×
                    </IconButton>
                </Box>

                <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none', mb: 1.5 }}>
                    <TableContainer sx={{
                        maxHeight: '60vh',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1
                    }}>
                        <Table stickyHeader aria-label="center candidates table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            sx={{ bgcolor: 'grey.100', color: 'grey.600', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap', px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'grey.300', cursor: 'default', userSelect: 'none' }}
                                            key={column.id}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading
                                    ? <TableRow>
                                        <TableCell colSpan={columns.length} sx={{ border: 'none', textAlign: 'center' }}>
                                            <CircularProgress size={24} />
                                        </TableCell>
                                    </TableRow>
                                    : !candidates?.length
                                        ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                            <TableCell colSpan={columns.length} sx={{ border: 'none', textAlign: 'center' }}>
                                                No records available
                                            </TableCell>
                                        </TableRow>
                                        : candidates.map((candidate, candidateIndex) => {
                                            return <TableRow hover={false} key={candidate.candidateId}>
                                                {columns.map((column) => {
                                                    return <TableCell key={column.id} sx={{
                                                        px: 1.5,
                                                        py: 1.125,
                                                        fontSize: 13,
                                                        whiteSpace: 'nowrap',
                                                        borderColor: 'grey.300'
                                                    }}>
                                                        {renderTableCell(column, candidate, candidateIndex)}
                                                    </TableCell>
                                                })}
                                            </TableRow>
                                        })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Pagination count={maxPages} page={currentPage} onChange={(e, value) => setCurrentPage(value)} shape="rounded" />
            </Box>
        </Modal>
    );
}
